/**
 * Role Service - Role-based access control management
 * Handles user roles, permissions, and access control
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { createComponentLogger } from '../utils/logger';
import { UserRole, ROLE_PERMISSIONS, RolePermissions } from '../types/roles';

const logger = createComponentLogger('RoleService');

/**
 * Get user's role from Firestore
 * @param uid - User ID
 * @returns User's role, defaults to 'student' if not found
 */
export async function getUserRole(uid: string): Promise<UserRole> {
  try {
    if (!uid) {
      logger.warn('getUserRole called with empty uid');
      return 'student';
    }

    const userRef = doc(db.value, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      const role = userDoc.data()?.role;
      if (role && isValidRole(role)) {
        return role as UserRole;
      }
    }

    logger.debug(`No role found for user ${uid}, defaulting to 'student'`);
    return 'student';
  } catch (error) {
    // S113: this returned 'student', which is a real answer to a question this
    // read could not answer. A teacher whose `users/{uid}` read was refused was
    // quietly shown the student experience — and Settings' own `.catch`, written
    // to say exactly that, was unreachable in production. Rule 8b: fix BOTH
    // ends. `RoleGuard` already catches and denies access, which is the right
    // direction for a guard; the two screens that display a role now get to say
    // they do not know.
    // S116: this interpolated the uid into the message. On a shared classroom
    // Chromebook a permission-denied wrote another account's identifier into a
    // console anybody can reopen, and no pattern can redact a bare uid.
    logger.error('Error fetching user role:', error);
    throw error instanceof Error ? error : new Error(String(error));
  }
}

/**
 * Set user's role - ADMIN FUNCTION
 * @param uid - User ID
 * @param role - Role to assign
 * @returns true if successful, false otherwise
 */
export async function setUserRole(uid: string, role: UserRole): Promise<boolean> {
  try {
    if (!uid) {
      logger.warn('setUserRole called with empty uid');
      return false;
    }

    if (!isValidRole(role)) {
      logger.warn(`Invalid role attempted: ${role}`);
      return false;
    }

    const userRef = doc(db.value, 'users', uid);

    // Try to update existing user doc
    try {
      await updateDoc(userRef, { role });
    } catch (error) {
      // If document doesn't exist, create it
      if ((error as { code?: string }).code === 'not-found') {
        await setDoc(userRef, { role, createdAt: new Date() }, { merge: true });
      } else {
        throw error;
      }
    }

    logger.info(`Role set to '${role}' for user ${uid}`);
    return true;
  } catch (error) {
    logger.error(`Error setting role for user ${uid}:`, error);
    return false;
  }
}

/**
 * Check if user has a specific permission
 * @param uid - User ID
 * @param permission - Permission key to check
 * @returns true if user has permission, false otherwise
 */
export async function hasPermission(
  uid: string,
  permission: keyof RolePermissions
): Promise<boolean> {
  try {
    const role = await getUserRole(uid);
    const permissions = getPermissions(role);
    return permissions[permission];
  } catch (error) {
    logger.error(`Error checking permission for user ${uid}:`, error);
    return false;
  }
}

/**
 * Get permissions object for a role
 * @param role - User role
 * @returns Permissions object for the role
 */
export function getPermissions(role: UserRole): RolePermissions {
  return ROLE_PERMISSIONS[role] || ROLE_PERMISSIONS.student;
}

/**
 * Check if a string is a valid UserRole
 * @param role - Role to validate
 * @returns true if valid role
 */
function isValidRole(role: unknown): role is UserRole {
  return ['student', 'teacher', 'admin', 'district_admin', 'super_admin', 'parent'].includes(String(role));
}

/**
 * Get all users with a specific role in a school
 * Note: This would typically be called on the backend for security
 * @param schoolId - School ID to filter by
 * @param _role - Role to filter by
 * @returns Array of user IDs with the specified role
 */
export async function getUsersByRole(schoolId: string, _role: UserRole): Promise<string[]> {
  try {
    if (!schoolId) {
      logger.warn('getUsersByRole called with empty schoolId');
      return [];
    }

    // In a production system, this should be a secure backend function
    // For now, we provide the interface but recommend using Cloud Functions
    logger.warn('getUsersByRole should be called via secure backend function, not from client');
    return [];
  } catch (error) {
    logger.error(`Error getting users by role:`, error);
    return [];
  }
}

/**
 * Check if user has any of the specified permissions
 * @param uid - User ID
 * @param permissions - Array of permission keys to check
 * @returns true if user has at least one of the permissions
 */
export async function hasAnyPermission(
  uid: string,
  permissions: (keyof RolePermissions)[]
): Promise<boolean> {
  try {
    const role = await getUserRole(uid);
    const userPermissions = getPermissions(role);
    return permissions.some((permission) => userPermissions[permission]);
  } catch (error) {
    logger.error(`Error checking any permission for user ${uid}:`, error);
    return false;
  }
}

/**
 * Check if user has all of the specified permissions
 * @param uid - User ID
 * @param permissions - Array of permission keys to check
 * @returns true if user has all of the permissions
 */
export async function hasAllPermissions(
  uid: string,
  permissions: (keyof RolePermissions)[]
): Promise<boolean> {
  try {
    const role = await getUserRole(uid);
    const userPermissions = getPermissions(role);
    return permissions.every((permission) => userPermissions[permission]);
  } catch (error) {
    logger.error(`Error checking all permissions for user ${uid}:`, error);
    return false;
  }
}

/**
 * Export singleton-like interface for role service
 */
export const roleService = {
  getUserRole,
  setUserRole,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getPermissions,
  getUsersByRole,
};
